
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pb, Subject } from '@/lib/pocketbase';

export const useSubjects = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['subjects'],
    queryFn: async () => {
      try {
        const subjects = await pb.collection('subjects').getList(1, 100, {
          filter: 'isActive = true',
          sort: 'displayOrder,name',
          requestKey: null // Disable auto-cancellation
        });
        console.log('🔍 useSubjects - Fetched subjects:', subjects.items);
        console.log('🔍 useSubjects - Approved subjects:', subjects.items.filter(s => s.category === 'approved'));
        return subjects.items as Subject[];
      } catch (error: any) {
        // Don't log expected auth warnings for public data
        if (error.status !== 0 && error.status !== 403 && error.status !== 401) {
          console.error('Error fetching subjects:', error);
        }
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const createSubject = useMutation({
    mutationFn: async (subjectData: Partial<Subject>) => {
      return await pb.collection('subjects').create(subjectData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
    },
  });

  const updateSubject = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Subject> }) => {
      return await pb.collection('subjects').update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
    },
  });

  const deleteSubject = useMutation({
    mutationFn: async (id: string) => {
      return await pb.collection('subjects').delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
    },
  });

  return {
    subjects: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    createSubject: createSubject.mutate,
    updateSubject: updateSubject.mutate,
    deleteSubject: deleteSubject.mutate,
    refetch: query.refetch,
  };
};
